/**
 * Sideindstillinger (12-08-2026): registry over de sider, der har et tandhjul
 * i headeren (indstillings-drawer) + felterne/defaults pr. side.
 *
 * Deles af server (validering + effektive værdier til beregninger/vagter) og
 * web (drawer-rendering + Indstillinger → Sideindstillinger-oversigten).
 *
 * PRINCIP: tabellen page_settings rummer KUN overrides — registry'et her er
 * sandheden om felter og defaults. Nye sider med indstillinger tilføjes HER
 * (server og web følger automatisk med; husk grants-reglerne er uændrede:
 * læsning = view-grant til siden, gem = edit-grant til siden).
 */

import {
  CAMPAIGN_EXCLUDED_ITEMS_DEFAULT,
  CAMPAIGN_EXCLUDED_ITEMS_HELP,
  CAMPAIGN_EXCLUDED_ITEMS_KEY,
  CAMPAIGN_EXCLUDED_ITEMS_LABEL,
} from './campaign-rules.js';
import { CLEARANCE_BLACK_NOV_2026 } from './clearance-exclusion-sets.js';
import {
  SYNC_DOCTOR_MAX_BUSY_PCT,
  getSyncSource,
  syncSettingsDefaults,
  syncSettingsFields,
} from './sync-schedules.js';

/** Felttyper drawer-rendereren forstår. 'select' ([D-285] fase 2, 09-09-2026):
 *  ét valg blandt faste muligheder (fx informeret-reglen), gemt som streng.
 *  'ruleSets' ([D-293], 14-09-2026): NAVNGIVNE regelsæt (fx "Black Nov 2026"),
 *  hvert med en liste af tekstregler "felt · operator · værdi" (fx "Varenavn
 *  begynder med Prøve"), gemt som array; hvilke felter en regel kan måle på,
 *  står i feltets `ruleFields`. Hvilket sæt der ANVENDES, er sidens eget
 *  filter (Lagerudsalg: `regelsaet=` i adressen), ikke en indstilling.
 *  'campaigns' ([D-297], 16-09-2026): annoncekampagner valgt på PLATFORMENS
 *  ID ("google_ads:16065121657"), aldrig på navn. Bureauet omdøber og samler
 *  kampagner; et navne-match fejler da tavst, et ID gør ikke. Hvilke platforme
 *  der kan vælges fra, står i feltets `providers`.
 *  'rules' ([D-476], 29-10-2026): ÉN liste af tekstregler som i et regelsæt,
 *  men uden navn og valg (fx Kampagners "Varer der aldrig får kampagnepris");
 *  alle reglerne gælder altid. Felterne står i `ruleFields`.
 *  'syncInterval' og 'syncIncremental' ([D-356], 02-10-2026): hvor ofte en
 *  3. part synkroniseres (minutter) og om kørslerne i løbet af dagen er
 *  inkrementelle. Feltet bærer `syncSource`; værdien gemmes IKKE i
 *  page_settings, men i kildens eget lager (sync_schedules), så en kilde der
 *  vises på flere sider altid har ét interval (shared/sync-schedules.ts). */
export type PageSettingFieldType =
  | 'number'
  | 'boolean'
  | 'select'
  | 'text'
  | 'ruleSets'
  | 'rules'
  | 'campaigns'
  | 'syncInterval'
  | 'syncIncremental';

/** Operatorer i en tekstregel ([D-293]). Sammenligningen er altid uden hensyn
 *  til store/små bogstaver og med trimmede ender. */
export const PAGE_SETTING_RULE_OPS = ['is', 'contains', 'startsWith', 'endsWith'] as const;
export type PageSettingRuleOp = (typeof PAGE_SETTING_RULE_OPS)[number];
export const PAGE_SETTING_RULE_OP_LABELS: Record<PageSettingRuleOp, string> = {
  is: 'er',
  contains: 'indeholder',
  startsWith: 'begynder med',
  endsWith: 'ender på',
};

/** Én tekstregel: `field` er en nøgle fra feltets `ruleFields`. */
export interface PageSettingRule {
  field: string;
  op: PageSettingRuleOp;
  value: string;
}

/** Et NAVNGIVET regelsæt ([D-293]): fx "Black Nov 2026" med 95 regler. `id`
 *  er stabilt (bruges i adresser og gemte valg), `name` er det brugeren ser
 *  og kan omdøbe. Matchning = OR over sættets regler. */
export interface PageSettingRuleSet {
  id: string;
  name: string;
  rules: PageSettingRule[];
}

/** Grænser for regelsæt: værn mod at en jsonb vokser ud over rimelighed. */
export const PAGE_SETTING_RULES_MAX = 300;
export const PAGE_SETTING_RULE_VALUE_MAX = 120;
export const PAGE_SETTING_RULE_SETS_MAX = 20;
export const PAGE_SETTING_RULE_SET_NAME_MAX = 60;
/** Sæt-id: små bogstaver/cifre/bindestreg, 1 til 40 tegn (adresse-venligt). */
export const PAGE_SETTING_RULE_SET_ID_RE = /^[a-z0-9][a-z0-9-]{0,39}$/;

/** Nyt sæt-id (8 hex-tegn); klienten kalder den ved "Tilføj regelsæt". */
export function newPageSettingRuleSetId(): string {
  let s = '';
  for (let i = 0; i < 8; i += 1) s += Math.floor(Math.random() * 16).toString(16);
  return s;
}

/** En gemt indstillingsværdi. Strenge findes KUN for 'select'-felter og
 *  saneres altid mod feltets `options` (en ukendt streng falder til default);
 *  arrays af regelsæt KUN for 'ruleSets'-felter (saneres mod `ruleFields` +
 *  operatorerne), arrays af regler KUN for 'rules'-felter ([D-476]), arrays
 *  af strenge KUN for 'campaigns'-felter (kampagne-nøgler, saneres mod
 *  `providers`). */
export type PageSettingValue = number | boolean | string | PageSettingRuleSet[] | PageSettingRule[] | string[];

/** Grænse for valgte kampagner i ét felt ([D-297]). */
export const PAGE_SETTING_CAMPAIGNS_MAX = 100;
/** Kampagne-nøgle: "<provider>:<platformens kampagne-id>". */
export const PAGE_SETTING_CAMPAIGN_KEY_RE = /^([a-z][a-z0-9_]{1,29}):([A-Za-z0-9_.-]{1,64})$/;

export function campaignKey(provider: string, externalCampaignId: string): string {
  return provider + ':' + externalCampaignId;
}

/** Del en kampagne-nøgle; null hvis den ikke har formen provider:id. */
export function parseCampaignKey(key: string): { provider: string; externalCampaignId: string } | null {
  const m = PAGE_SETTING_CAMPAIGN_KEY_RE.exec(key);
  return m ? { provider: m[1]!, externalCampaignId: m[2]! } : null;
}

export interface PageSettingOption {
  value: string;
  label: string;
  /** Kort forklaring, vises som tooltip/hjælp ved valget. */
  help?: string;
}

export interface PageSettingFieldDef {
  /** Nøgle i settings-jsonb'en. */
  key: string;
  label: string;
  /** Kort hjælpetekst under feltet (valgfri). */
  help?: string;
  type: PageSettingFieldType;
  /** Kun for type 'number': clamp-grænser + enhed. */
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  /** Kun for type 'select': de tilladte værdier. */
  options?: PageSettingOption[];
  /** Kun for type 'ruleSets' og 'rules': de felter en regel kan måle på (value = nøgle,
   *  label = det brugeren ser, help = fx hvilke værdier feltet kan have). */
  ruleFields?: readonly PageSettingOption[];
  /** Kun for type 'campaigns': de annonceplatforme der kan vælges fra
   *  (ad_insights_daily.provider, fx 'google_ads'). */
  providers?: readonly string[];
  /** Kun for type 'campaigns': regex-kilde (uden flag, matches uden hensyn
   *  til store/små bogstaver) for kampagnenavne der LIGNER rollen. Bruges
   *  ALDRIG til at udvælge, kun til at gøre opmærksom på en ny kampagne med
   *  forbrug, der ikke er valgt ([D-297]). */
  suggestPattern?: string;
  /** Kun for 'syncInterval'/'syncIncremental' ([D-356]): kilden i SYNC_SOURCES. */
  syncSource?: string;
  /** Kun for type 'text' ([D-371]): maks. længde (default 200) og valgfrit
   *  regex-mønster (uden flag) værdien SKAL matche — ellers beholdes default.
   *  Et mønster gør at fx en liste-nøgle eller dato aldrig kan gemmes halvt. */
  maxLength?: number;
  pattern?: string;
  placeholder?: string;
  /** Kun for type 'text' ([D-468]): et tomt felt er en gyldig værdi (fx "ingen kategorier") i stedet for at falde tilbage til default. */
  allowEmpty?: boolean;
}

/**
 * Passer en tekstregel på en værdi? Uden hensyn til store/små bogstaver og
 * med trimmede ender, så "prøve" rammer "Prøve-Terra …" og " -P" rammer "-P".
 * null/undefined (fx web-status der ikke er synket) passer aldrig.
 */
export function pageSettingRuleMatches(rule: PageSettingRule, fieldValue: string | null | undefined): boolean {
  if (fieldValue == null) return false;
  const v = fieldValue.trim().toLowerCase();
  const needle = rule.value.trim().toLowerCase();
  if (!needle) return false;
  switch (rule.op) {
    case 'is': return v === needle;
    case 'contains': return v.includes(needle);
    case 'startsWith': return v.startsWith(needle);
    case 'endsWith': return v.endsWith(needle);
    default: return false;
  }
}

/** Sanér en rå regelliste mod feltets `ruleFields`: ukendt felt/operator,
 *  tom værdi og dubletter droppes; værdier trimmes og kappes; listen kappes. */
export function sanitizePageSettingRules(field: PageSettingFieldDef, raw: unknown): PageSettingRule[] {
  if (!Array.isArray(raw)) return [];
  const allowed = new Set((field.ruleFields ?? []).map((f) => f.value));
  const out: PageSettingRule[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const { field: f, op, value } = r as Record<string, unknown>;
    if (typeof f !== 'string' || !allowed.has(f)) continue;
    if (typeof op !== 'string' || !(PAGE_SETTING_RULE_OPS as readonly string[]).includes(op)) continue;
    if (typeof value !== 'string') continue;
    const v = value.trim().slice(0, PAGE_SETTING_RULE_VALUE_MAX);
    if (!v) continue;
    const key = `${f}|${op}|${v.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ field: f, op: op as PageSettingRuleOp, value: v });
    if (out.length >= PAGE_SETTING_RULES_MAX) break;
  }
  return out;
}

/** Sanér en rå liste af regelsæt: ugyldigt/dubleret id og tomt navn dropper
 *  sættet; navnet trimmes og kappes; reglerne saneres pr. sæt (et sæt med 0
 *  gyldige regler BEHOLDES, så man kan oprette et sæt og fylde det senere). */
export function sanitizePageSettingRuleSets(field: PageSettingFieldDef, raw: unknown): PageSettingRuleSet[] {
  if (!Array.isArray(raw)) return [];
  const out: PageSettingRuleSet[] = [];
  const ids = new Set<string>();
  for (const s of raw) {
    if (!s || typeof s !== 'object') continue;
    const { id, name, rules } = s as Record<string, unknown>;
    if (typeof id !== 'string' || !PAGE_SETTING_RULE_SET_ID_RE.test(id) || ids.has(id)) continue;
    if (typeof name !== 'string') continue;
    const n = name.trim().slice(0, PAGE_SETTING_RULE_SET_NAME_MAX);
    if (!n) continue;
    ids.add(id);
    out.push({ id, name: n, rules: sanitizePageSettingRules(field, rules) });
    if (out.length >= PAGE_SETTING_RULE_SETS_MAX) break;
  }
  return out;
}

/** Sanér en rå liste af kampagne-nøgler ([D-297]): kun strenge på formen
 *  provider:id med en provider fra feltets `providers`; dubletter droppes,
 *  listen kappes. En TOM liste er gyldig (ingen kampagner valgt). */
export function sanitizePageSettingCampaigns(field: PageSettingFieldDef, raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const allowed = new Set(field.providers ?? []);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const v of raw) {
    if (typeof v !== 'string') continue;
    const key = v.trim();
    const parsed = parseCampaignKey(key);
    if (!parsed || !allowed.has(parsed.provider) || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
    if (out.length >= PAGE_SETTING_CAMPAIGNS_MAX) break;
  }
  return out;
}

/** En annoncekampagne et 'campaigns'-felt kan vælges fra ([D-297]). */
export interface AdCampaignOptionDto {
  key: string;
  provider: string;
  externalCampaignId: string;
  /** null når kampagnen kun findes i ad_insights_daily (ikke i ad_campaigns). */
  name: string | null;
  status: string | null;
  firstSpendDate: string | null;
  lastSpendDate: string | null;
  spend90Kr: number;
}

/** En note ved tallene når et ID-valg kan være forældet ([D-297]):
 *  'stopped' = platformens valgte kampagner har ikke brugt penge i 14 dage
 *  (date = seneste forbrug); 'new_match' = en ikke-valgt kampagne der ligner
 *  rollen fik sit første forbrug inden for 30 dage (date = første forbrug). */
export interface CampaignSelectionNoteDto {
  kind: 'stopped' | 'new_match';
  provider: string;
  date: string | null;
  campaigns: Array<{ key: string; name: string | null }>;
}

/** Visningsnavne for annonceplatformene i kampagnevalget. */
export const AD_PLATFORM_LABELS: Readonly<Record<string, string>> = {
  google_ads: 'Google Ads',
  bing_ads: 'Microsoft Ads',
  meta_ads: 'Meta Ads',
  tiktok_ads: 'TikTok Ads',
};
export function adPlatformLabel(provider: string): string {
  return AD_PLATFORM_LABELS[provider] ?? provider;
}

export interface CampaignSelectionDto {
  routePath: string;
  fieldKey: string;
  keys: string[];
  selected: AdCampaignOptionDto[];
  unknownKeys: string[];
  notes: CampaignSelectionNoteDto[];
}

export interface PageSettingsDef {
  /** Sidens route_path — SAMME nøgle som page-kataloget/grants ([D-141]). */
  routePath: string;
  /** Sidens titel (vises i drawer + oversigt). */
  title: string;
  /** Emne-gruppering på Sideindstillinger-oversigten. */
  topic: string;
  /** Kort beskrivelse af hvad indstillingerne styrer. */
  description: string;
  fields: PageSettingFieldDef[];
  defaults: Record<string, PageSettingValue>;
}

/** Customer Match ([D-285]): de 3 udadvendte tilstande. Deles med serverens
 *  config (lib/customer-match/config.ts) og siden. */
export const CUSTOMER_MATCH_OUTBOUND_MODES = ['off', 'validate', 'live'] as const;
/** Restparti-kopien på Lagervarer ([D-455], §59), styrer Opret (Tjek læser kun og virker
 *  altid): Fra = slået fra, Validér = kun planen, Til = skriver i BC. Deles med serveren
 *  (routes/items-clearance.ts). */
export const RESTPARTI_MODES = ['off', 'validate', 'live'] as const;
export type RestpartiMode = (typeof RESTPARTI_MODES)[number];
/** Restparti-periode ([D-457]): Følg kampagnen = -R venter On Hold og spærret for salg til
 *  kampagnestart; Bypass = -R bliver aktiv på webshoppen og kan sælges med det samme (test af
 *  flowet i Production). Deles med serveren (routes/items-clearance.ts). */
export const RESTPARTI_PERIODS = ['campaign', 'bypass'] as const;
export type RestpartiPeriod = (typeof RESTPARTI_PERIODS)[number];
export type CustomerMatchOutboundMode = (typeof CUSTOMER_MATCH_OUTBOUND_MODES)[number];

/** Customer Match ([D-285]): hvornår en kunde regnes som informeret om
 *  listebaseret markedsføring (vilkårsteksten rettet 07-09-2026). */
export const CUSTOMER_MATCH_INFORMED_RULES = ['first_invoice_after', 'any_invoice_after', 'all'] as const;
export type CustomerMatchInformedRule = (typeof CUSTOMER_MATCH_INFORMED_RULES)[number];

/** Varer → Lagervarer ([D-300]; hed Lagerudsalg på /items/clearance indtil
 *  17-09-2026, [D-291]/[D-293]): sidens sti + de felter en undtagelsesregel
 *  kan måle på. Nøglerne er ClearanceItem-felterne (server og web deler dem,
 *  så en regel gemt her betyder præcis det samme begge steder).
 *  ⚠ page_settings er nøglet på stien: rækken for /items/clearance flyttes
 *  til /items/inventory i prod med SQL FØR deploy (§21, [D-300]). */
export const INVENTORY_ROUTE_PATH = '/items/inventory';
export const CLEARANCE_RULE_FIELDS: readonly PageSettingOption[] = [
  { value: 'vendor', label: 'Leverandør' },
  { value: 'series', label: 'Produktserie' },
  { value: 'type', label: 'Produkttype' },
  { value: 'name', label: 'Varenavn' },
  { value: 'number', label: 'Varenummer' },
  { value: 'web', label: 'Web-status' },
];
export type ClearanceRuleField = 'vendor' | 'series' | 'type' | 'name' | 'number' | 'web';

/** Organic search: brand-kampagnerne hvis klikpris værdisætter de organiske
 *  brand-sessions ([D-297]). */
export const ORGANIC_SEARCH_ROUTE_PATH = '/marketing/organic-search';
/** Analysen Indretningskonsulent ([D-280]): kampagnerne hvis forbrug er
 *  grundlaget for kr pr. booking ([D-297]). */
export const IK_ANALYSIS_ROUTE_PATH = '/analyses/c29cc5a7d2083735ad04d281ea2b3f89';

/** Kunder → Kunderejsen ([D-417]): reglerne for købsforløb, Kompletsalg/
 *  Styksalg, tabt kunde og CLV. Serveren (lib/customer-journey.ts) læser de
 *  EFFEKTIVE værdier og sender dem med i svaret, så siden aldrig hårdkoder en
 *  grænse. Defaults = Charlies bestilling 19-10-2026 + ~AOV-vinduet ([D-341]). */
export const CUSTOMER_JOURNEY_ROUTE_PATH = '/customers/journey';
export const CUSTOMER_JOURNEY_DEFAULTS = {
  windowDays: 90,
  minPurchaseKr: 100,
  kompletMinInclKr: 25000,
  unitMaxInclKr: 15000,
  lostQuantilePct: 90,
  minN: 30,
  clvYears: 5,
  leadFollowUpDays: 180,
} as const;

/** Inbound → Leads ([D-371]): Klaviyo-lead-agentens centrale regler bor i
 *  sidens tandhjul. Defaults = Mikkels/Adtimizes instruks 07-10-2026; server-
 *  agenten (server/src/lib/klaviyo-leads.ts) læser de EFFEKTIVE værdier her. */
export const LEADS_ROUTE_PATH = '/inbound/leads';
export const KLAVIYO_LEADS_DEFAULTS = {
  klaviyoTransferEnabled: true,
  klaviyoTypeformListId: 'Wu8TS5',
  klaviyoImportListId: 'XSc2m5',
  klaviyoCutoverDate: '2026-10-07',
  klaviyoSinceDate: '2026-07-07',
  klaviyoBcStatusEvents: true,
  klaviyoQuoteEventsAllCustomers: true,
  klaviyoMeetingStatus: true,
} as const;
const KLAVIYO_LIST_ID_PATTERN = '[A-Za-z0-9]{6}';
const DATE_PATTERN = '\\d{4}-\\d{2}-\\d{2}';
/** [D-393]: én mailadresse (grov form-validering; serveren sender aldrig til en tom). */
const EMAIL_PATTERN = '[^@\\s]+@[^@\\s]+\\.[^@\\s]+';
/** [D-468]: WooCommerce-kategori-id'er adskilt med komma, fx "5805" eller "5805, 167". */
const WP_CATEGORY_IDS_PATTERN = '\\d{1,9}(\\s*,\\s*\\d{1,9})*';

/** Alle sider med indstillinger. Rækkefølgen styrer oversigtens visning. */
export const PAGE_SETTINGS_DEFS: readonly PageSettingsDef[] = [
  {
    routePath: '/accounting/expense-docs',
    title: 'Udgiftsbilag',
    topic: 'Regnskab',
    description:
      'Værnene for udgiftsbilag-agentens automatiske videresendelse til bogholderiet ([D-391]). Bilag der falder uden for værnene, går altid i godkendelseskøen.',
    fields: [
      {
        key: 'autoLimitDkk',
        label: 'Grænse for automatisk videresendelse',
        help: 'Bilag med totalbeløb UNDER grænsen kan videresendes automatisk; alt på eller over kræver godkendelse.',
        type: 'number',
        min: 0,
        max: 50000,
        step: 100,
        unit: 'kr',
      },
      {
        key: 'patternTolerancePct',
        label: 'Tolerance mod leverandørens mønster',
        help: 'Hvor meget beløbet må afvige fra leverandørens sædvanlige opkrævning (medianen af de videresendte bilag) i begge retninger.',
        type: 'number',
        min: 10,
        max: 200,
        step: 5,
        unit: '%',
      },
      {
        key: 'duplicateWindowDays',
        label: 'Vindue for dobbelt bilag',
        help: 'Samme beløb fra samme leverandør inden for vinduet regnes som muligt dobbelt bilag og kræver godkendelse.',
        type: 'number',
        min: 1,
        max: 30,
        step: 1,
        unit: 'dage',
      },
      {
        key: 'lookbackDays',
        label: 'Tilbageblik i postkasserne',
        help: 'Hvor langt tilbage agenten kigger efter bilags-mails og videresendelser i Sendt post.',
        type: 'number',
        min: 7,
        max: 90,
        step: 1,
        unit: 'dage',
      },
      {
        key: 'forwardTo',
        label: 'Videresend til',
        help: 'Bogholderi-adressen bilagene videresendes til. Kun de 2 adresser er mulige (afsender-værnet).',
        type: 'select',
        options: [
          { value: 'invoice@badogfliser.dk', label: 'invoice@badogfliser.dk' },
          { value: 'bogholder@badogfliser.dk', label: 'bogholder@badogfliser.dk' },
        ],
      },
    ],
    defaults: {
      autoLimitDkk: 5000,
      patternTolerancePct: 50,
      duplicateWindowDays: 7,
      lookbackDays: 30,
      forwardTo: 'invoice@badogfliser.dk',
    },
  },
  {
    routePath: '/ops/actions',
    title: 'Handlinger',
    topic: 'Driftstatus',
    description:
      'Platformvagtens rammer ([D-402]): hvor langt tilbage agenten kigger i marketing@/ai-agent@, og lofterne for sprogmodel-kald pr. kørsel. Et varsel der rammer loftet, efterlades urørt og tages i næste kørsel.',
    fields: [
      {
        key: 'lookbackDays',
        label: 'Tilbageblik i postkasserne',
        help: 'Hvor langt tilbage agenten kigger efter platform-mails. Allerede screenede mails koster intet igen (dedup).',
        type: 'number',
        min: 7,
        max: 90,
        step: 1,
        unit: 'dage',
      },
      {
        key: 'maxScreenPerRun',
        label: 'Screeninger pr. kørsel',
        help: 'Loft for billige screening-kald (er mailen et varsel?) pr. kørsel på tværs af begge postkasser.',
        type: 'number',
        min: 10,
        max: 200,
        step: 10,
      },
      {
        key: 'maxAssessPerRun',
        label: 'Vurderinger pr. kørsel',
        help: 'Loft for grundige vurderings-kald (varslet mod integrationsprofilen) pr. kørsel.',
        type: 'number',
        min: 1,
        max: 25,
        step: 1,
      },
    ],
    defaults: {
      lookbackDays: 30,
      maxScreenPerRun: 60,
      maxAssessPerRun: 10,
    },
  },
  {
    routePath: CUSTOMER_JOURNEY_ROUTE_PATH,
    title: 'Kunderejsen',
    topic: 'Kunder',
    description:
      'Reglerne bag kunderejsen: hvordan ordrer samles i købsforløb, hvornår et forløb er Kompletsalg eller Styksalg, hvornår en kunde regnes som tabt, og CLV-horisonten. Alle tal på siden regnes om, når reglerne ændres.',
    fields: [
      {
        key: 'kompletMinInclKr',
        label: 'Kompletsalg fra',
        help: 'Et købsforløb er Kompletsalg, når værdien inkl. moms er mindst dette beløb (og ingen vare har en for høj stykpris, se næste felt). Alt andet er Styksalg.',
        type: 'number',
        min: 1000,
        max: 500000,
        step: 1000,
        unit: 'kr inkl. moms',
      },
      {
        key: 'unitMaxInclKr',
        label: 'Højeste stykpris i et Kompletsalg',
        help: 'Har forløbet mindst 1 varelinje hvor 1 stk koster mere end dette (fx et armatur til 38.000 kr), er forløbet Styksalg, uanset værdien. Stykprisen måles på BC-vareposteringerne (salgsbeløb ÷ antal).',
        type: 'number',
        min: 1000,
        max: 500000,
        step: 1000,
        unit: 'kr inkl. moms',
      },
      {
        key: 'windowDays',
        label: 'Købsforløbets vindue',
        help: 'Privatkunder: kundens største ordre og alle kundens ordrer og kreditnotaer så mange dage før og efter er ét købsforløb (~AOV-reglen, D-341). En ordre senere i forløbet er følgesalg. Erhvervskunder regnes pr. ordre.',
        type: 'number',
        min: 7,
        max: 365,
        step: 1,
        unit: 'dage',
      },
      {
        key: 'minPurchaseKr',
        label: 'Mindste køb',
        help: 'Et forløb under dette beløb ekskl. moms (typisk fliseprøver) er et prøvekøb og ikke et køb.',
        type: 'number',
        min: 0,
        max: 5000,
        step: 10,
        unit: 'kr ekskl. moms',
      },
      {
        key: 'lostQuantilePct',
        label: 'Tabt kunde efter',
        help: 'En kunde uden nyt forløb regnes som tabt, når der er gået længere tid end den tid hvor denne andel af de kunder der kom igen, var kommet (målt pr. Kompletsalg og Styksalg over hele historikken).',
        type: 'number',
        min: 50,
        max: 99,
        step: 1,
        unit: '% af genkøbene',
      },
      {
        key: 'clvYears',
        label: 'CLV-horisont',
        help: 'Antal år efter første køb som CLV opgøres over.',
        type: 'number',
        min: 1,
        max: 10,
        step: 1,
        unit: 'år',
      },
      {
        key: 'leadFollowUpDays',
        label: 'Opfølgning på kontakter',
        help: 'Fra kontakt til kunde: en kontakt der ikke har købt inden for så mange dage, regnes som ikke konverteret. Kontakter yngre end dette tæller ikke med i andelen.',
        type: 'number',
        min: 30,
        max: 730,
        step: 1,
        unit: 'dage',
      },
      {
        key: 'minN',
        label: 'Mindste gruppe',
        help: 'Grupper med færre kunder vises med ~ og fremhæves aldrig som bedst.',
        type: 'number',
        min: 5,
        max: 500,
        step: 1,
        unit: 'kunder',
      },
    ],
    defaults: { ...CUSTOMER_JOURNEY_DEFAULTS },
  },
  {
    routePath: '/customers/discount-groups',
    title: 'Rabatgrupper',
    topic: 'Kunder',
    description:
      'Kriterier for hvornår en kunde uden debitorrabatgruppe udvælges som kandidat af rabatgruppe-vagten (påvirker både siden og den daglige mail).',
    fields: [
      {
        key: 'windowMonths',
        label: 'Detektionsvindue',
        help: 'Fakturaer med bogføringsdato i de seneste N måneder indgår.',
        type: 'number',
        min: 1,
        max: 24,
        step: 1,
        unit: 'måneder',
      },
      {
        key: 'minInvoices',
        label: 'Mindste antal fakturaer',
        help: 'Kunden skal have mindst så mange fakturaer i vinduet.',
        type: 'number',
        min: 1,
        max: 50,
        step: 1,
        unit: 'fakturaer',
      },
      {
        key: 'minDiscountSharePct',
        label: 'Mindste rabatandel',
        help: 'Samlet linjerabat i % af brutto (netto + rabat) i vinduet.',
        type: 'number',
        min: 1,
        max: 100,
        step: 1,
        unit: '%',
      },
    ],
    defaults: { windowMonths: 6, minInvoices: 4, minDiscountSharePct: 25 },
  },
  {
    routePath: '/marketing/campaigns',
    title: 'Kampagner',
    topic: 'Marketing',
    description:
      'Kriterier for den datadrevne anbefaling af 100/20-andelen (ikke-øremærket spend): baseline-vinduet der måler kanalernes normale niveau, mindstekravet til grundlaget og sæson-advarslens følsomhed. Desuden de WP-kategorier som alle tilbudsvarer får, og de varer der aldrig får kampagnepris.',
    fields: [
      {
        key: 'suggestBaselineDays',
        label: 'Baseline-vindue',
        help: 'Kampagne-frie dage op til kampagnestart som "normalt niveau" måles på. Målt på Black Week 2025: spend-løftet var stabilt på tværs af 30/60/90 dage.',
        type: 'number',
        min: 14,
        max: 180,
        step: 1,
        unit: 'dage',
      },
      {
        key: 'suggestMinFreeDays',
        label: 'Mindste antal kampagne-frie dage',
        help: 'Under dette antal frie dage i baseline-vinduet gives intet forslag (for tyndt grundlag).',
        type: 'number',
        min: 7,
        max: 90,
        step: 1,
        unit: 'dage',
      },
      {
        key: 'seasonWarnDeviationPct',
        label: 'Sæson-advarslens følsomhed',
        help: 'Advar når baseline-niveauet afviger mere end dette fra kontoens 12 mdr\'s niveau (fx kampagne lige efter en flad december).',
        type: 'number',
        min: 5,
        max: 75,
        step: 1,
        unit: '%',
      },
      {
        // [D-468] (Charlie 28-10-2026): gælder alle kampagner, kun i perioden.
        key: 'wpOfferCategoryIds',
        label: 'WP-kategorier på tilbudsvarer',
        help: 'Kategori-id\'er i WooCommerce, adskilt med komma, som alle tilbudsvarer og bundles i en kampagne får, mens kampagnen kører (som tilbudspriserne). De tilføjes oven i produktets egne kategorier, og ved kampagnens slut fjernes kun dem, Cockpit selv har tilføjet. Kategorier der kun gælder én kampagne (fx pr. primærkategori), sættes på kampagnens side under WP-kategorier. Tomt = ingen. 5805 = Tilbud.',
        type: 'text',
        maxLength: 100,
        pattern: WP_CATEGORY_IDS_PATTERN,
        placeholder: 'fx 5805',
        allowEmpty: true,
      },
      {
        // [D-476] (Charlie 29-10-2026): erstatter regelsættet fra Lagervarer, som Kun lagervarer brugte.
        key: CAMPAIGN_EXCLUDED_ITEMS_KEY,
        label: CAMPAIGN_EXCLUDED_ITEMS_LABEL,
        help: CAMPAIGN_EXCLUDED_ITEMS_HELP,
        type: 'rules',
        ruleFields: CLEARANCE_RULE_FIELDS,
      },
    ],
    defaults: {
      suggestBaselineDays: 60,
      suggestMinFreeDays: 14,
      seasonWarnDeviationPct: 25,
      wpOfferCategoryIds: '5805',
      [CAMPAIGN_EXCLUDED_ITEMS_KEY]: CAMPAIGN_EXCLUDED_ITEMS_DEFAULT.map((r) => ({ ...r })) satisfies PageSettingRule[],
    },
  },
  // Customer Match ([D-285] fase 2, 09-09-2026): kill-switch, informeret-regel
  // og segment-vinduer som side-indstillinger (§46). Serveren læser dem med
  // getEffectivePageSettings i både API'et og natjobbet, så et menneske kan
  // ændre dem uden kode. Udadvendt = default FRA; sættes til "Validér" eller
  // "Til" KUN med Charlies GO til jobbet som helhed (D-285 udestående b).
  {
    routePath: '/marketing/customer-match',
    title: 'Customer Match',
    topic: 'Marketing',
    description:
      'Styrer hvad Cockpit sender til Google Ads og Meta ud fra BC-kunderne: den udadvendte kontakt (fra, validér, til), hvornår en kunde regnes som informeret om listebaseret markedsføring, og segmenternes vinduer. Intet forlader Cockpit, så længe kontakten står på Fra.',
    fields: [
      {
        key: 'outboundMode',
        label: 'Udadvendt kontakt',
        help: 'Fra: intet sendes (natjobbet bygger kun grundlaget). Validér: Google kaldes med validateOnly (intet gemmes hos Google), Meta springes over. Til: lister og konverteringer sendes.',
        type: 'select',
        options: [
          { value: 'off', label: 'Fra' },
          { value: 'validate', label: 'Validér (Google validateOnly, Meta springes over)' },
          { value: 'live', label: 'Til (sender)' },
        ],
      },
      {
        key: 'informedRule',
        label: 'Informeret-regel',
        help: 'Vilkårsteksten blev rettet 07-09-2026. BC har ingen oprettelsesdato, så reglen måles på bogførte fakturaer (tilbud og ordrebekræftelser henviser til betingelserne).',
        type: 'select',
        options: [
          { value: 'first_invoice_after', label: 'Første faktura på/efter 07-09-2026 (strengest)' },
          { value: 'any_invoice_after', label: 'Mindst 1 faktura på/efter 07-09-2026' },
          { value: 'all', label: 'Alle kunder (kræver efterfølgende oplysning)' },
        ],
      },
      {
        key: 'audiencesEnabled',
        label: 'Send målgruppe-lister',
        help: 'Kundelisterne (købt, win-back, top 20 %, aktive) sendes og vedligeholdes hos Google og Meta. Kræver at kontakten står på Validér eller Til.',
        type: 'boolean',
      },
      {
        key: 'conversionsEnabled',
        label: 'Send butikskøb som konverteringer',
        help: 'Butiksordrer (aldrig web) sendes med værdi ekskl. moms som offline-konverteringer, 1 gang pr. ordre. Kræver at kontakten står på Validér eller Til.',
        type: 'boolean',
      },
      {
        key: 'metaValueInclVat',
        label: 'Meta-værdi inkl. moms',
        help: 'Google får altid værdien ekskl. moms. Meta følger den konvention web-købet bruger dér; afventer Simons svar (Morningtrain). Fra = ekskl. moms.',
        type: 'boolean',
      },
      {
        key: 'boughtMonths',
        label: 'Købt inden for',
        help: 'Hovedlisten: kunder med mindst 1 varekøb inden for vinduet.',
        type: 'number',
        min: 1,
        max: 60,
        step: 1,
        unit: 'måneder',
      },
      {
        key: 'winbackToMonths',
        label: 'Win-back til',
        help: 'Win-back: købt for "Købt inden for" til dette antal måneder siden, og ikke siden. Skal være større end "Købt inden for".',
        type: 'number',
        min: 2,
        max: 120,
        step: 1,
        unit: 'måneder',
      },
      {
        key: 'activeDays',
        label: 'Aktive (suppression)',
        help: 'Kunder der har købt inden for vinduet; listen bruges til at UNDGÅ at vise erhvervelses-annoncer til dem.',
        type: 'number',
        min: 1,
        max: 365,
        step: 1,
        unit: 'dage',
      },
      {
        key: 'topSharePct',
        label: 'Top-andel',
        help: 'Top-listen: de kunder der udgør den øverste andel efter omsætning ekskl. moms i win-back-vinduet, pr. gruppe.',
        type: 'number',
        min: 1,
        max: 100,
        step: 1,
        unit: '%',
      },
      {
        key: 'includeBusiness',
        label: 'ERHVERV-lister',
        help: 'Byg og send ERHVERV som egne lister (aldrig blandet med PRIVAT). Fælles postkasser udelades altid.',
        type: 'boolean',
      },
    ],
    defaults: {
      outboundMode: 'off',
      informedRule: 'first_invoice_after',
      audiencesEnabled: true,
      conversionsEnabled: true,
      metaValueInclVat: false,
      boughtMonths: 12,
      winbackToMonths: 24,
      activeDays: 90,
      topSharePct: 20,
      includeBusiness: true,
    },
  },
  {
    routePath: '/marketing/competitors',
    title: 'Konkurrenter',
    topic: 'Marketing',
    description:
      'Kriterier for konkurrent-benchmarken ([D-292]): dækningsgraden der oversætter en konkurrents bruttofortjeneste til ~omsætning, hvor mange af vores søgeord natagenten slår op live (koster penge pr. opslag), og hvornår et domæne er "nok til stede" til at blive foreslået som konkurrent.',
    fields: [
      {
        key: 'grossMarginPct',
        label: 'Dækningsgrad til ~omsætning',
        help: 'Regnskabsklasse B oplyser ikke nettoomsætning. ~Omsætning = bruttofortjeneste ÷ dækningsgrad. Husets regnskab: 43 %.',
        type: 'number',
        min: 10,
        max: 90,
        step: 1,
        unit: '%',
      },
      {
        key: 'serpKeywords',
        label: 'Søgeord i det natlige SERP-opslag',
        help: 'Vores største ikke-brand-søgeord (Search Console, 90 dage) slås op live hver nat: organisk top 20, tekstannoncer og Shopping-gitteret. Ca. 0,03 kr pr. søgeord pr. nat.',
        type: 'number',
        min: 10,
        max: 200,
        step: 5,
        unit: 'søgeord',
      },
      {
        key: 'minSharedKeywords',
        label: 'Mindste antal fælles søgeord',
        help: 'Et domæne fra DataForSEO\'s konkurrentliste foreslås når det deler mindst så mange søgeord med os.',
        type: 'number',
        min: 50,
        max: 2000,
        step: 50,
        unit: 'søgeord',
      },
      {
        key: 'minSerpKeywordHits',
        label: 'Mindste antal søgeord med tilstedeværelse',
        help: 'Et domæne foreslås når det står i organisk top 10, med en tekstannonce eller i Shopping-gitteret på mindst så mange af de opslåede søgeord.',
        type: 'number',
        min: 1,
        max: 50,
        step: 1,
        unit: 'søgeord',
      },
    ],
    defaults: { grossMarginPct: 43, serpKeywords: 40, minSharedKeywords: 300, minSerpKeywordHits: 3 },
  },
  // Organic SoMe → Overvågning ([D-383], 09-10-2026): forslags-agentens
  // kriterier. Detektionen af hændelser har ingen indstillinger (den sporer
  // altid alle overvågede personer i de lokale spejl).
  {
    routePath: '/marketing/organic-social/monitoring',
    title: 'Organic SoMe · Overvågning',
    topic: 'Marketing',
    description:
      'Kriterier for forslags-agenten ([D-383]): hvor langt tilbage nattens signaler (nye tilbud, ordrer, køb og Klaviyo-profiler) hentes, influencer-grænsen for følgere, og om sprogmodel-screeningen af nye navne er slået til. Hændelses-sporingen af personer der allerede er på listen, kører altid og påvirkes ikke af indstillingerne.',
    fields: [
      {
        key: 'lookbackDays',
        label: 'Vindue for nye signaler',
        help: 'Hvor mange dage tilbage forslags-agenten kigger efter nye tilbud, ordrer, køb og Klaviyo-profiler. Allerede vurderede kandidater screenes ikke igen.',
        type: 'number',
        min: 1,
        max: 30,
        step: 1,
        unit: 'dage',
      },
      {
        key: 'minFollowers',
        label: 'Influencer-grænsen',
        help: 'Mindst så mange følgere på modellens Instagram-bud (Meta business_discovery) kvalificerer alene til et forslag. Buddet er aldrig verificeret som personens egen profil; mennesket afgør forslaget.',
        type: 'number',
        min: 1000,
        max: 1_000_000,
        step: 1000,
        unit: 'følgere',
      },
      {
        key: 'llmSuggest',
        label: 'Sprogmodel-forslag',
        help: 'Slået til screener sprogmodellen nye kontakters navne (kun navn, by og signaltype sendes) for om de er offentligt kendte i Danmark. Slået fra kommer der ingen forslag; hændelses-sporingen kører uændret.',
        type: 'boolean',
      },
      {
        key: 'llmEnrich',
        label: 'Automatisk berigelse',
        help: 'Slået til finder sprogmodellen via websøgning hvad personen er kendt for og personens SoMe-profiler med følgertal ([D-392]); Instagram-følgertal verificeres hos Meta. Kører for nye personer på listen (dagligt og ved Kør nu). Slået fra beriges kun manuelt med knappen i person-draweren.',
        type: 'boolean',
      },
    ],
    defaults: { lookbackDays: 7, minFollowers: 10_000, llmSuggest: true, llmEnrich: true },
  },
  // Lagerudsalg ([D-293], 14-09-2026): undtagelsesregler. Benjamins manuelle
  // gennemgang af 12 mdr-listen (1.016 varer → 805) viste at 57 % af de
  // fjernede varer var 3 hele leverandører (Cristina, Kaiping Qioio, Tangshan
  // Sunrise: 120 af 120 fjernet), 15 % var prøver og resten tilbehør kendt på
  // navnet (bordplade, rosette, bruseslange, hængsler, monteringssæt,
  // skylleknap). Alt sammen tekstregler på leverandør/varenavn/varenummer, så
  // reglerne bor her og kan ændres uden kode. Defaults = de regler der ramte
  // 100 % (leverandørerne) + prøver (samples sælges aldrig på udsalg).
  {
    routePath: INVENTORY_ROUTE_PATH,
    title: 'Lagervarer',
    topic: 'Varer',
    description:
      'Undtagelser fra listen på Varer → Lagervarer som navngivne regelsæt (fx "Black Nov 2026"): varer der opfylder filtrene (fx intet salg i den valgte periode), men ikke skal på udsalg, fx tilbehør, prøver eller hele leverandører. En vare udelades når mindst 1 regel i det anvendte sæt passer. Der kan oprettes flere regelsæt, men kun ét kan være valgt ad gangen; det vælges i filterpanelet under Undtagelser (klik på det valgte slår det fra). Antallet af udeladte varer vises altid på siden, og de kan efterses med linket Vis dem ved listen.',
    fields: [
      {
        key: 'exclusionSets',
        label: 'Regelsæt',
        help: 'Hvert regelsæt har et navn (fx en kampagne) og sine egne regler; hvilket sæt der anvendes, vælges i filterpanelet under Undtagelser (ét ad gangen). Sammenligningen skelner ikke mellem store og små bogstaver. Leverandør, produktserie og produkttype skal ramme hele værdien som den står på siden (brug "er"); varenavn og varenummer rammes typisk med "indeholder", "begynder med" eller "ender på". Web-status er Active, Deactivated, Delete eller On Hold.',
        type: 'ruleSets',
        ruleFields: CLEARANCE_RULE_FIELDS,
      },
      {
        key: 'restpartiMode',
        label: 'Restparti-kopiering',
        help: 'Styrer Opret under Tilpas → Opret restparti (Tjek læser kun og virker altid). Fra: Opret er slået fra. Validér: Opret viser kun, hvad der ville ske, og skriver intet. Til: Opret laver -R-kopien i BC (On Hold og spærret for salg til kampagnestart) og lægger det disponible lager i varekladden FLYTRESTPA. Til i BC Production kræver Charlies OK (§20).',
        type: 'select',
        options: [
          { value: 'off', label: 'Fra' },
          { value: 'validate', label: 'Validér (Opret skriver intet)' },
          { value: 'live', label: 'Til (Opret skriver i BC)' },
        ],
      },
      {
        key: 'restpartiPeriod',
        label: 'Restparti-periode',
        help: 'Følg kampagnen: -R venter On Hold på webshoppen og er spærret for salg i BC til kampagnestart. Bypass: -R oprettes aktiv på webshoppen og kan sælges med det samme, uden at følge en kampagnes periode. Bypass er til at afprøve flowet i Production og skal stilles tilbage på Følg kampagnen bagefter. Rabatten lægges ikke på af Opret.',
        type: 'select',
        options: [
          { value: 'campaign', label: 'Følg kampagnen' },
          { value: 'bypass', label: 'Bypass: aktiv med det samme (test)' },
        ],
      },
    ],
    defaults: {
      exclusionSets: [CLEARANCE_BLACK_NOV_2026] satisfies PageSettingRuleSet[],
      restpartiMode: 'off',
      restpartiPeriod: 'campaign',
    },
  },
  // Organic search ([D-297], 16-09-2026): brand-kampagnerne valgt på ID. Før
  // fandt organisk ROI dem på ordet "brand" i navnet, og det ramte også
  // "Search | Brands" (leverandørmærker: vola, kuma, ss304 i GA4's utm_term),
  // mens en omdøbt brandkampagne ville være faldet ud tavst. Morningtrain
  // omlagde budgetterne 16-09-2026 og har allerede omdøbt kampagnerne én gang
  // ("MB | Search | Brand - B&F" til "Search | Brand B&F", samme ID).
  {
    routePath: ORGANIC_SEARCH_ROUTE_PATH,
    title: 'Organic search',
    topic: 'Marketing',
    description:
      'Hvilke annoncekampagner der er vores egen brandkampagne. Deres klikpris værdisætter de organiske brand-sessions i erstatningsværdien. Kampagnerne vælges på ID, så et nyt kampagnenavn ændrer intet; en ny kampagne skal vælges her.',
    fields: [
      {
        key: 'brandCampaigns',
        label: 'Brand-kampagner',
        help: 'Kun kampagner på vores eget navn (Bad og Fliser), ikke kampagner på leverandørmærker. Siden gør opmærksom på en valgt kampagne der er holdt op med at bruge penge, og på en ny kampagne med "brand" i navnet.',
        type: 'campaigns',
        providers: ['google_ads', 'bing_ads'],
        suggestPattern: 'brand|bad ?(og|&) ?fliser|b&f',
      },
    ],
    defaults: {
      brandCampaigns: ['google_ads:16065121657', 'bing_ads:604252105'],
    },
  },
  // Analysen Indretningskonsulent ([D-297]): kampagnerne med trafik til
  // /indretningskonsulent/, valgt på ID. Defaulten er PRÆCIS de 11 kampagner
  // (Google + Meta, også de stoppede fra 2024) som navne-matchet
  // "%indretningskonsulent%" ramte i prod 16-09-2026, så analysens perioder og
  // målepunkter er uændrede ved skiftet.
  {
    routePath: IK_ANALYSIS_ROUTE_PATH,
    title: 'Indretningskonsulent',
    topic: 'Analyser',
    description:
      'Hvilke annoncekampagner der fører trafik til indretningskonsulent-siden. Deres forbrug er grundlaget for annonceforbrug og kr pr. booking i analysens perioder, ugegraf og målepunkter. Kampagnerne vælges på ID, så et nyt kampagnenavn ændrer intet.',
    fields: [
      {
        key: 'campaigns',
        label: 'Kampagner med indretningskonsulent-trafik',
        help: 'Stoppede kampagner skal blive på listen, så længe deres forbrug hører til en målt periode. Analysen gør opmærksom på en ny kampagne med "indretningskonsulent" i navnet, der ikke er valgt.',
        type: 'campaigns',
        providers: ['google_ads', 'bing_ads', 'meta_ads', 'tiktok_ads'],
        suggestPattern: 'indretningskonsulent',
      },
    ],
    defaults: {
      campaigns: [
        'google_ads:24026388842',
        'google_ads:24156523371',
        'google_ads:1525638137',
        'meta_ads:120249734906400346',
        'meta_ads:120251112968190346',
        'meta_ads:23857627575860345',
        'meta_ads:23844045143980345',
        'meta_ads:23844849055320345',
        'meta_ads:23844972371580345',
        'meta_ads:23847069846420345',
        'meta_ads:23848964285860345',
      ],
    },
  },
  {
    // [D-553] Relewise, Index: Cockpits afsendelse af produktdata til Relewise.
    // Charlie 05-11-2026: "Byg og deploy" efter afstemningen af designet, så
    // standarden er Til. Sikringen er feltporten: et felt sendes kun, når
    // Cockpits oversættelse har vist 0 forskelle mod pluginets værdier.
    routePath: '/webshop/relewise/index',
    title: 'Relewise · Index',
    topic: 'Webshop',
    description:
      'Styrer om Cockpit sender produktdata til Relewise, straks efter en ændring i WP (Cockpits egen eller fra BC-overførslen) og når vagten finder en vare, hvor Relewise er bagud. Kun felter, der har bestået feltporten, sendes.',
    fields: [
      {
        key: 'pushMode',
        label: 'Afsendelse til Relewise',
        help: 'Fra: intet sendes, vagten måler kun. Tørkørsel: afsendelserne bygges og logges, men sendes ikke. Til: sendes.',
        type: 'select',
        options: [
          { value: 'off', label: 'Fra' },
          { value: 'validate', label: 'Tørkørsel (logges, sendes ikke)' },
          { value: 'live', label: 'Til (sender)' },
        ],
      },
    ],
    defaults: { pushMode: 'live' },
  },
  {
    // [D-310] Pagespeed-vagtens regler. Charlie 26-09-2026: mail ved ændringer
    // over 10 % i PageScore og 20 % i LCP, TBT eller CLS, og 'juster tærsklen,
    // så den giver mening' hvis det giver for mange mails. Standarden er valgt
    // ud fra simuleringer på 32 dages prod-målinger (se D-310). Læses af
    // notifikationen pagespeed_watch.
    routePath: '/webshop/cro/pagespeed',
    title: 'CRO · Pagespeed',
    topic: 'Webshop',
    description:
      'Regler for Pagespeed-vagten (Indstillinger → Notifikationer). Standarden er valgt ud fra 32 dages målinger i prod, så mailen kommer når noget reelt bliver dårligere, ikke hver morgen af tilfældige udsving i Lighthouse.',
    fields: [
      {
        key: 'watchLevel',
        label: 'Vurdér',
        help: 'Forsiden og landingssiderne er altid 1 side hver.',
        type: 'select',
        options: [
          {
            value: 'sidetype',
            label: 'Sidetype (gns. af de 10 sider)',
            help: 'Kategorier, produkter og indholdssider vurderes som gennemsnittet af deres 10 sider; mailen viser de sider der har rykket sig mest.',
          },
          {
            value: 'side',
            label: 'Hver side for sig',
            help: 'Hver af de op til 68 målinger vurderes for sig. Giver markant flere mails, fordi én side svinger meget fra nat til nat.',
          },
        ],
      },
      {
        key: 'watchScorePct',
        label: 'Ændring i PageScore',
        help: 'Mindste ændring mod gennemsnittet før, før der kan sendes mail.',
        type: 'number',
        min: 1,
        max: 100,
        step: 1,
        unit: '%',
      },
      {
        key: 'watchMetricPct',
        label: 'Ændring i LCP, TBT eller CLS',
        help: 'Mindste ændring i et af de 3 tal. TBT skal desuden ændre sig mindst 50 ms og CLS mindst 0,01.',
        type: 'number',
        min: 1,
        max: 500,
        step: 1,
        unit: '%',
      },
      {
        key: 'watchRequireCategory',
        label: 'Kun når Googles grænse krydses',
        help: 'Tallet skal også krydse en af Googles grænser (fx LCP over 2,5 s eller 4 s, CLS over 0,1 eller 0,25, TBT over 200 eller 600 ms, PageScore under 90 eller 50). Uden det giver små udsving på gode tal mail.',
        type: 'boolean',
      },
      {
        key: 'watchSmoothNights',
        label: 'Gennemsnit af de seneste',
        help: 'Nattens tal er gennemsnittet af så mange nætter, så én urolig nat ikke giver mail.',
        type: 'number',
        min: 1,
        max: 7,
        step: 1,
        unit: 'nætter',
      },
      {
        key: 'watchBaselineNights',
        label: 'Sammenlign med',
        help: 'Gennemsnittet af så mange nætter før. Mindst 3 skal findes.',
        type: 'number',
        min: 3,
        max: 30,
        step: 1,
        unit: 'nætter',
      },
      {
        key: 'watchDirection',
        label: 'Retning',
        type: 'select',
        options: [
          { value: 'vaerre', label: 'Kun forværring', help: 'Kun lavere score eller højere LCP, TBT eller CLS giver mail.' },
          { value: 'begge', label: 'Begge veje', help: 'Også forbedringer giver mail.' },
        ],
      },
    ],
    defaults: {
      watchLevel: 'sidetype',
      watchScorePct: 10,
      watchMetricPct: 20,
      watchRequireCategory: true,
      watchSmoothNights: 3,
      watchBaselineNights: 7,
      watchDirection: 'vaerre',
    },
  },
  // ── Synk-intervaller ([D-356], 02-10-2026) ────────────────────────────────
  // Felterne dannes af registret i sync-schedules.ts; Sync-doktoren kan hæve
  // intervallet eller slå inkrementel synkronisering til og mailer om det.
  {
    routePath: '/marketing/affiliates/loyalty-key',
    title: 'Loyalty Key',
    topic: 'Marketing',
    description:
      `Hvor ofte transaktionerne hentes fra Loyalty Key, og kontrollen af butikshandler kører. Natkørslen kører altid; et kortere interval lægger kørsler i løbet af dagen oveni, målt fra seneste kørsel. Sync-doktoren hæver intervallet, hvis en kørsel tager mere end ${SYNC_DOCTOR_MAX_BUSY_PCT} % af det, og mailer om det.`,
    fields: syncSettingsFields('/marketing/affiliates/loyalty-key'),
    defaults: syncSettingsDefaults('/marketing/affiliates/loyalty-key'),
  },
  {
    routePath: '/marketing/qr-codes',
    title: 'QR-koder',
    topic: 'Marketing',
    description:
      `Hvor ofte QR-koderne og deres scanninger hentes fra QR TIGER. Natkørslen kører altid; et kortere interval lægger kørsler i løbet af dagen oveni, målt fra seneste kørsel. Sync-doktoren hæver intervallet, hvis en kørsel tager mere end ${SYNC_DOCTOR_MAX_BUSY_PCT} % af det, og mailer om det.`,
    fields: syncSettingsFields('/marketing/qr-codes'),
    defaults: syncSettingsDefaults('/marketing/qr-codes'),
  },
  {
    // [D-546]: kampagnemails hver time; profiler og flowmails kun om natten.
    routePath: '/marketing/email',
    title: 'Email',
    topic: 'Marketing',
    description:
      `Hvor ofte kampagnemails med status og rapport-tal hentes fra Klaviyo. Profilerne (hele modtagerlisten) og flowmailenes ugetal hentes kun af natkørslen. Sync-doktoren hæver intervallet, hvis en kørsel tager mere end ${SYNC_DOCTOR_MAX_BUSY_PCT} % af det, og mailer om det.`,
    fields: syncSettingsFields('/marketing/email'),
    defaults: syncSettingsDefaults('/marketing/email'),
  },
  // Inbound → Telefon ([D-393]): TDC-mail-agentens selvfejlsøgning. Når en
  // rapport er fejlscopet eller en uge/måned mangler i spejlet, rekvirerer
  // agenten selv hos TDC-supporten fra ai-agent@ og følger op. Fail-closed:
  // uden supportadressen sendes intet eksternt — fejlmailen til Charlie bærer
  // i stedet en færdig rekvisitionstekst.
  {
    routePath: '/inbound/phone',
    title: 'Telefon',
    topic: 'Inbound',
    description:
      'TDC-mail-agentens selvfejlsøgning ([D-393]): er en rapport fejlscopet (fx et 1-times vindue), eller mangler en hel uge eller måned i spejlet, fejlmelder agenten selv til TDC-supporten fra ai-agent@badogfliser.dk, beder om rettelse af den planlagte rapport og gen-fremsendelse af de manglende perioder, og følger op. Uden supportadressen sendes intet eksternt; fejlmailen bærer i stedet en færdig rekvisitionstekst.',
    fields: [
      {
        key: 'tdcSupportEmail',
        label: 'TDC-supportens mailadresse',
        help: 'Adressen agentens rekvisitioner sendes til (fra ai-agent@badogfliser.dk; TDC-supportens svar og gen-fremsendte rapporter til ai-agent@ indlæses automatisk). Tom = der sendes intet eksternt, og fejlmailen indeholder i stedet en færdig rekvisitionstekst.',
        type: 'text',
        maxLength: 100,
        pattern: EMAIL_PATTERN,
        placeholder: 'fx erhvervssupport@tdc.dk',
      },
      {
        key: 'tdcAutoRequest',
        label: 'Automatisk rekvisition',
        help: 'Slået til sender agenten selv rekvisitionen til TDC-supporten, når en rapport er fejlscopet eller en periode mangler (højst 1 pr. kø og periode). Slået fra får du fejlmailen med en færdig rekvisitionstekst i stedet.',
        type: 'boolean',
      },
      {
        key: 'tdcFollowupDays',
        label: 'Opfølgning efter',
        help: 'Er den rekvirerede rapport ikke kommet så mange dage efter rekvisitionen, får du 1 eskalationsmail pr. kø og periode, så du kan følge op direkte hos TDC.',
        type: 'number',
        min: 2,
        max: 21,
        step: 1,
        unit: 'dage',
      },
    ],
    defaults: { tdcSupportEmail: '', tdcAutoRequest: true, tdcFollowupDays: 5 },
  },
  {
    routePath: '/inbound/bookings',
    title: 'Bookinger',
    topic: 'Inbound',
    description:
      `Hvor ofte bookingerne hentes fra Calendly og formular-svarene fra Typeform. Natkørslen kører altid; et kortere interval lægger kørsler i løbet af dagen oveni, målt fra seneste kørsel. Typeform-intervallet er det samme som på Henvendelser. Sync-doktoren hæver intervallet eller slår inkrementel synkronisering til, hvis en kørsel tager mere end ${SYNC_DOCTOR_MAX_BUSY_PCT} % af intervallet, og mailer om det.`,
    fields: syncSettingsFields('/inbound/bookings'),
    defaults: syncSettingsDefaults('/inbound/bookings'),
  },
  {
    routePath: '/inbound/inquiries',
    title: 'Henvendelser',
    topic: 'Inbound',
    description:
      `Hvor ofte formular-svarene hentes fra Typeform og FluentForms. Natkørslen kører altid; et kortere interval lægger kørsler i løbet af dagen oveni, målt fra seneste kørsel. Typeform-intervallet er det samme som på Bookinger. Sync-doktoren hæver intervallet, hvis en kørsel tager mere end ${SYNC_DOCTOR_MAX_BUSY_PCT} % af det, og mailer om det.`,
    fields: syncSettingsFields('/inbound/inquiries'),
    defaults: syncSettingsDefaults('/inbound/inquiries'),
  },
  {
    routePath: LEADS_ROUTE_PATH,
    title: 'Leads',
    topic: 'Inbound',
    description:
      'Klaviyo-lead-agentens centrale regler ([D-371], Mikkels instruks fra Adtimize 07-10-2026) og hvor ofte kilderne hentes. Reglerne styrer HVAD der overføres; metric-navnene i Klaviyo er faste og må ikke ændres uden Adtimizes viden.',
    fields: [
      {
        key: 'klaviyoTransferEnabled',
        label: 'Overførsel til Klaviyo',
        help: 'Slået fra sender agenten INTET til Klaviyo (hverken tilmeldinger, liste-import eller events). Allerede overførte data røres ikke. Nye handlinger samles op igen når den slås til.',
        type: 'boolean',
      },
      {
        key: 'klaviyoTypeformListId',
        label: 'Klaviyo-liste: nye tilmeldinger',
        help: 'Liste-id (6 tegn, fra Klaviyo → Lists & Segments) som NYE Typeform-svar med samtykke-ja tilmeldes som Subscribed. Mikkels valg: Wu8TS5.',
        type: 'text',
        maxLength: 6,
        pattern: KLAVIYO_LIST_ID_PATTERN,
        placeholder: 'Wu8TS5',
      },
      {
        key: 'klaviyoImportListId',
        label: 'Klaviyo-liste: historisk import',
        help: 'Liste-id som de historiske leads importeres til; med samtykke som Subscribed (med oprindeligt samtykketidspunkt), uden samtykke UDEN tilmelding. Mikkels valg: XSc2m5 ("Import").',
        type: 'text',
        maxLength: 6,
        pattern: KLAVIYO_LIST_ID_PATTERN,
        placeholder: 'XSc2m5',
      },
      {
        key: 'klaviyoCutoverDate',
        label: 'Skæringsdato (gamle/nye leads)',
        help: 'Leads registreret FØR denne dato er historisk import (Import-listen, ingen flows udløses); fra og med den følger de tilmeldings-reglen (Typeform-listen). ÅÅÅÅ-MM-DD.',
        type: 'text',
        maxLength: 10,
        pattern: DATE_PATTERN,
        placeholder: '2026-10-07',
      },
      {
        key: 'klaviyoSinceDate',
        label: 'Startdato for leads',
        help: 'Leads registreret før denne dato overføres slet ikke (Charlies bestilling: fra 7. juli 2026). ÅÅÅÅ-MM-DD.',
        type: 'text',
        maxLength: 10,
        pattern: DATE_PATTERN,
        placeholder: '2026-07-07',
      },
      {
        key: 'klaviyoBcStatusEvents',
        label: 'BC-status-events',
        help: 'Events på profilen når leadets BC-kunde får udstedt et tilbud, accepterer en ordre, faktureres eller køber en 3D-tegning (varenr 00010000/00010001) efter første kontakt. Mikkels flows bruger dem til at UNDLADE opfølgning.',
        type: 'boolean',
      },
      {
        key: 'klaviyoMeetingStatus',
        label: 'Mødestatus (aflyst uden ny aftale)',
        help: 'Slået til får leads der har aflyst et Calendly-møde profil-egenskaben Mødestatus i Klaviyo: "Aflyst uden ny aftale", indtil de booker igen gennem bookingflowet ELLER bliver inviteret til et manuelt oprettet møde i en af vores Outlook-kalendere (info@ og alle andre @badogfliser.dk-postkasser); så skifter den til "Ny aftale efter aflysning". Manuelle Outlook-møder sendes desuden som eventet Møde indkaldt (Outlook). Mødestatus sendes kun, når Outlook-kalenderne er synkroniseret fuldt inden for 36 timer.',
        type: 'boolean',
      },
      {
        key: 'klaviyoQuoteEventsAllCustomers',
        label: 'Tilbud og ordre: alle BC-kunder',
        help: 'Slået til sendes "Tilbud udstedt" for ALLE kunder med et tilbud i BC, ikke kun leads, og "Ordre accepteret" for alle ordrer der stammer fra et tilbud (dateret på accepten), både til leadets email og til emailen på BC-kundekortet. Eventene bærer egenskaben Lead-kilde (Typeform, FluentForms, Calendly eller Business Central), så Klaviyo kan udelukke kunder i en igangværende tilbudsfase fra rabatkoder og produkttilbud og ophæve det, når tilbuddet er accepteret. Samle-/kontantkonti og interne adresser sendes aldrig. Kræver BC-status-events.',
        type: 'boolean',
      },
      ...syncSettingsFields(LEADS_ROUTE_PATH),
    ],
    defaults: {
      ...KLAVIYO_LEADS_DEFAULTS,
      ...syncSettingsDefaults(LEADS_ROUTE_PATH),
    },
  },
  {
    routePath: '/stores/price-tags/signs',
    title: 'Prisskilte',
    topic: 'Butikker',
    description:
      `Hvor ofte ændrede varer sendes til Breece Cloud, når automatikken er slået til på Regelsæt. Det er samme værdi som Interval på Regelsæt, og en ændring står i regelsættets ændringslog. Sync-doktoren hæver intervallet, hvis en kørsel tager mere end ${SYNC_DOCTOR_MAX_BUSY_PCT} % af det, og mailer om det.`,
    fields: syncSettingsFields('/stores/price-tags/signs'),
    defaults: syncSettingsDefaults('/stores/price-tags/signs'),
  },
];

/**
 * Slå en sides definition op — undefined hvis siden ingen indstillinger har.
 *
 * Eksakt match først; ellers ARVER en parametriseret UNDERSIDE forælderens
 * definition (længste prefix vinder). [D-262]: hver kampagne har sin egen side
 * `/marketing/campaigns/<id>`, men anbefalings-kriterierne (baseline-vindue
 * m.m.) er fælles for dem alle og bor derfor på listen. Uden arven ville
 * tandhjulet på en kampagneside stå tomt, og §46's regel — "indstillinger sættes
 * i tandhjulet, aldrig i kode" — kun gælde oversigten. Samme princip som
 * adgangen: en parametriseret underside hører til sin forælder-side.
 *
 * ⚠ Gemme-vejen er upåvirket: der gemmes altid på `def.routePath` (forælderen),
 * så serverens edit-gate måler mod den side, indstillingerne faktisk hører til.
 */
export function getPageSettingsDef(routePath: string): PageSettingsDef | undefined {
  const exact = PAGE_SETTINGS_DEFS.find((d) => d.routePath === routePath);
  if (exact) return exact;
  return PAGE_SETTINGS_DEFS.filter((d) => routePath.startsWith(d.routePath + '/')).sort(
    (a, b) => b.routePath.length - a.routePath.length,
  )[0];
}

/**
 * Sanér gemte overrides mod definitionen: ukendte nøgler droppes, tal clampes
 * til [min, max] og ikke-numeriske/forkerte typer falder tilbage til default.
 * Returnerer ALTID et komplet settings-objekt (defaults + gyldige overrides).
 */
export function resolvePageSettings(
  def: PageSettingsDef,
  stored: Record<string, unknown> | null | undefined,
): Record<string, PageSettingValue> {
  const out: Record<string, PageSettingValue> = { ...def.defaults };
  if (!stored) return out;
  for (const field of def.fields) {
    const raw = stored[field.key];
    if (field.type === 'number' || field.type === 'syncInterval') {
      const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : NaN;
      if (!Number.isNaN(n)) {
        const min = field.min ?? -Infinity;
        const max = field.max ?? Infinity;
        const v = field.type === 'syncInterval' ? Math.round(n) : n;
        out[field.key] = Math.min(max, Math.max(min, v));
      }
    } else if (field.type === 'boolean') {
      if (typeof raw === 'boolean') out[field.key] = raw;
    } else if (field.type === 'syncIncremental') {
      // Et flueben på en kilde der ikke kan hentes inkrementelt, gemmes aldrig som til.
      if (typeof raw === 'boolean') out[field.key] = getSyncSource(field.syncSource ?? '')?.incremental === 'option' && raw;
    } else if (field.type === 'text') {
      // Trim + kap; matcher værdien ikke feltets mønster, beholdes default —
      // en halv liste-nøgle eller dato må aldrig nå en beregning ([D-371]).
      if (typeof raw === 'string') {
        const v = raw.trim().slice(0, field.maxLength ?? 200);
        if (v !== '' && (!field.pattern || new RegExp(`^(?:${field.pattern})$`).test(v))) {
          out[field.key] = v;
        } else if (v === '' && field.allowEmpty) {
          out[field.key] = '';
        }
      }
    } else if (field.type === 'select') {
      // Kun en KENDT mulighed accepteres; alt andet beholder default. Det er
      // saneringen der gør at en beregning aldrig ser fx en stavefejl som
      // "live " og tolker den som noget tredje.
      if (typeof raw === 'string' && (field.options ?? []).some((o) => o.value === raw)) {
        out[field.key] = raw;
      }
    } else if (field.type === 'ruleSets') {
      // En gemt liste (også en TOM) erstatter defaulten: brugeren skal kunne
      // slette alle standard-regelsæt. Kun når nøglen slet ikke er gemt,
      // gælder registry'ets default.
      if (Array.isArray(raw)) out[field.key] = sanitizePageSettingRuleSets(field, raw);
    } else if (field.type === 'rules') {
      // [D-476]: som regelsæt erstatter en gemt liste (også en tom) defaulten.
      if (Array.isArray(raw)) out[field.key] = sanitizePageSettingRules(field, raw);
    } else if (field.type === 'campaigns') {
      // Som regelsæt: en gemt liste (også en tom) erstatter defaulten.
      if (Array.isArray(raw)) out[field.key] = sanitizePageSettingCampaigns(field, raw);
    }
  }
  return out;
}

/** [D-468]: kategori-id'erne fra feltet "WP-kategorier på tilbudsvarer" (tomt = ingen). */
export function parseWpCategoryIds(value: unknown): number[] {
  if (typeof value !== 'string') return [];
  const ids = value.split(',').map((s) => Number(s.trim())).filter((n) => Number.isInteger(n) && n > 0);
  return [...new Set(ids)];
}
